import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { WerklijnContextMenuDetails } from '@/components/contextMenu/mwdContextMenu/types';
import { AdvancedSearchData } from '@/components/search/types';
import { usePostQuery } from '@/queries/usePostQuery';
import useClientState from '@/state/useClientState';
import { WerklijnenInline } from '@/types/werklijnenInline';

import { selectedStateToContextData } from '../state/actions/helpers/actionStore';
import { searchRequestConnectedWerklijnen } from '../utils/searchRequests';

const useMwdActions = () => {
  const { post } = usePostQuery<AdvancedSearchData, WerklijnenInline>('/werklijnen/zoek');
  const clearSearch = useClientState((state) => state.clearSearch);
  const voegMeerdereWerklijnenToe = useClientState((state) => state.voegMeerdereWerklijnenToe);
  const deselectAll = useClientState((state) => state.deselectAll);
  const vervangAlleWerklijnen = useClientState((state) => state.vervangAlleWerklijnen);
  const verwijderMeerdereWerklijnen = useClientState((state) => state.verwijderMeerdereWerklijnen);

  // TO-DO what does subId do?
  const subId = useClientState(useShallow((state) => Object.keys(state.werklijnenSubs)[0]));

  const toonGekoppeldeWerklijnen = useCallback(
    (details: WerklijnContextMenuDetails | undefined | { treinnummer: string; treindatum: string }) => async () => {
      if (!details || !('treinnummer' in details) || !('treindatum' in details)) return;
      const { treinnummer, treindatum } = details;
      if (!treinnummer || !treindatum) return;
      const data = await post(searchRequestConnectedWerklijnen(treinnummer, treindatum));
      if (!data || !('werklijnen' in data) || !data.werklijnen) return;
      // Set werklijnen to state.
      voegMeerdereWerklijnenToe(subId, data.werklijnen, true);
    },
    [post, subId, voegMeerdereWerklijnenToe],
  );
  /**
   * Vraag de gekoppelde werklijnen op voor het treinnummer van de inzet.
   * actionKey: gekoppelde-werklijnen
   * shortKey: Alt+T
   */
  const gekoppeldeWerklijnenTonen = useCallback(async () => {
    const selectedDeeltrajectContext = selectedStateToContextData(useClientState.getState());
    if (
      !selectedDeeltrajectContext ||
      !selectedDeeltrajectContext.details ||
      (selectedDeeltrajectContext.details as WerklijnContextMenuDetails)?.eenheidnummer === 'MANCO'
    )
      return;
    await Promise.all([toonGekoppeldeWerklijnen(selectedDeeltrajectContext.details)(), deselectAll()]);
  }, [toonGekoppeldeWerklijnen, deselectAll]);

  /**
   * Verwijder geselecteerde werklijnen uit het Mwd.
   * actionKey: verwijderen-mwd.
   * Optionally move to new hook useWerklijnenActions
   */
  const verwijderGeselecteerdeWerklijnen = useCallback(async () => {
    const selectedWerklijnen = useClientState.getState().selected.werklijnen;
    verwijderMeerdereWerklijnen(selectedWerklijnen);
    await deselectAll();
  }, [verwijderMeerdereWerklijnen, deselectAll]);

  /**
   * Verwijder alle werklijnen uit het Mwd.
   * Optionally move to new hook useWerklijnenActions
   */
  const verwijderWerklijnen = useCallback(async () => {
    vervangAlleWerklijnen([]);
    await deselectAll();
    clearSearch();
  }, [vervangAlleWerklijnen, deselectAll, clearSearch]);

  return {
    toonGekoppeldeWerklijnen,
    gekoppeldeWerklijnenTonen,
    verwijderGeselecteerdeWerklijnen,
    verwijderWerklijnen,
  };
};
export default useMwdActions;
